import React, { useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { chain, isEmpty } from 'lodash';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { Tooltip } from '@fiverr-private/fit';
import { getCategoryKey } from '../../utils';
import { customOrderPropsType, gigType } from '../GigServiceCard/propTypes';
import { RequestButton } from './RequestButton';

import './index.scss';

const T_PREFIX = 'listings.custom_order_card';
const SHOW_CATEGORIES_COUNT = 2;

const InlineDotSeparator = ({ isVertical }) => (
    <span className={classnames('inline-dot-separator m-r-8', { 'm-l-8': !isVertical })} />
);

InlineDotSeparator.propTypes = {
    isVertical: PropTypes.bool,
};

export const CustomOrderCard = ({
    className,
    gigs,
    sellerDisplayName,
    sellerUsername,
    sellerImage,
    isVertical,
    isUserPending,
    openUserActivateModal,
    currency,
    source,
    customOrderProps,
}) => {
    const subCategories = useMemo(
        () =>
            chain(gigs)
                .map((item) => ({
                    categoryId: item.category_id,
                    subCategoryId: item.sub_category_id,
                    nestedSubCategoryId: item.nested_sub_category_id,
                }))
                .map(({ categoryId, subCategoryId, nestedSubCategoryId }) =>
                    translate(getCategoryKey({ categoryId, subCategoryId, nestedSubCategoryId }))
                )
                .uniq()
                .sortBy((name) => name.length)
                .value(),
        [gigs]
    );

    const servicesNodes = subCategories.slice(0, SHOW_CATEGORIES_COUNT).map((text, idx) => (
        <div key={`${text}_wrapper`}>
            {!isVertical ? (
                !!idx && <InlineDotSeparator isVertical={isVertical} />
            ) : (
                <InlineDotSeparator isVertical={isVertical} />
            )}
            <span>{text}</span>
        </div>
    ));

    const hiddenServices = subCategories.slice(Math.min(SHOW_CATEGORIES_COUNT, subCategories.length));

    if (!isEmpty(hiddenServices)) {
        const hiddenServicesNode = (
            <>
                {hiddenServices.map((text) => (
                    <div key={text}>{text}</div>
                ))}
            </>
        );

        servicesNodes.push(
            <>
                <Tooltip content={hiddenServicesNode}>
                    {!isVertical && <InlineDotSeparator />}
                    <span key="n_more">
                        <I18n k={`${T_PREFIX}.n_more`} params={{ count: hiddenServices.length }} />
                    </span>
                </Tooltip>
            </>
        );
    }

    return (
        <div className={classnames(className, 'gig-custom-order-card flex-justify-between box-shadow-z1')}>
            <div className="text-container flex flex-col">
                <div className="font-domaine tbody-3 text-bold">
                    <div className="text-container--title">
                        <I18n
                            k={`${T_PREFIX}.title`}
                            templates={{
                                italic: (text) => <i>{text}</i>,
                            }}
                        />
                    </div>
                    <div>
                        <I18n k={`${T_PREFIX}.sub_title`} params={{ username: sellerDisplayName || sellerUsername }} />
                    </div>
                </div>
                <div
                    className={classnames('flex co-text-darker text-semi-bold', {
                        'flex-start': !isVertical,
                        'flex-row': !isVertical,
                        'flex-col': isVertical,
                    })}
                >
                    {servicesNodes}
                </div>
            </div>
            <div className="flex flex-end">
                {customOrderProps?.requestButton ? (
                    customOrderProps.requestButton
                ) : (
                    <RequestButton
                        sellerUsername={sellerUsername}
                        openUserActivateModal={openUserActivateModal}
                        isUserPending={isUserPending}
                        sellerDisplayName={sellerDisplayName}
                        sellerImage={sellerImage}
                        currency={currency}
                        source={source}
                    />
                )}
            </div>
        </div>
    );
};

CustomOrderCard.propTypes = {
    className: PropTypes.string,
    isVertical: PropTypes.bool,
    isUserPending: PropTypes.bool,
    openUserActivateModal: PropTypes.func,
    gigs: PropTypes.arrayOf(gigType),
    customOrderProps: customOrderPropsType,
    ...RequestButton.propTypes,
};
